import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import SEOHead from '../components/seo/SEOHead';
import useScrollToTop from '../hooks/useScrollToTop';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const formRef = useRef();
  
  useScrollToTop();

  // Get the reCaptcha site key
  const getRecaptchaSiteKey = () => {
    // Production site key
    return '6LeyxMMrAAAAAC9d4Hjj-Ilo1UDUUQ4JixzfJf00';
  };

  // Load reCaptcha script and set up explicit rendering
  useEffect(() => {
    // Make callback functions global for reCaptcha
    window.onRecaptchaCallback = (response) => {
      console.log('reCaptcha callback:', response);
      setRecaptchaToken(response);
    };

    window.onRecaptchaExpired = () => {
      console.log('reCaptcha expired');
      setRecaptchaToken('');
    };

    window.onRecaptchaError = (error) => {
      console.log('reCaptcha error:', error);
      setRecaptchaToken('');
    };

    // Function to render the reCaptcha widget
    const renderRecaptcha = () => {
      const container = document.getElementById('recaptcha-container');
      if (container && window.grecaptcha) {
        // Check if widget is already rendered in this container
        if (container.children.length > 0) {
          console.log('reCaptcha widget already rendered in this container');
          setRecaptchaLoaded(true);
          return;
        }
        
        try {
          const widgetId = window.grecaptcha.render(container, {
            'sitekey': getRecaptchaSiteKey(),
            'callback': 'onRecaptchaCallback',
            'expired-callback': 'onRecaptchaExpired',
            'error-callback': 'onRecaptchaError'
          });
          setRecaptchaWidgetId(widgetId);
          setRecaptchaLoaded(true);
          console.log('reCaptcha widget rendered with ID:', widgetId);
        } catch (error) {
          console.error('Error rendering reCaptcha widget:', error);
        }
      }
    };

    // Check if reCaptcha script is already loaded
    if (window.grecaptcha) {
      console.log('reCaptcha already loaded, rendering immediately');
      window.grecaptcha.ready(renderRecaptcha);
    } else {
      // Load reCaptcha script dynamically
      console.log('Loading reCaptcha script...');
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('reCaptcha script loaded');
        window.grecaptcha.ready(renderRecaptcha);
      };
      script.onerror = () => {
        console.error('Failed to load reCaptcha script');
        setRecaptchaLoaded(false);
      };
      document.head.appendChild(script);
    }

    return () => {
      delete window.onRecaptchaCallback;
      delete window.onRecaptchaExpired;
      delete window.onRecaptchaError;
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Check if reCaptcha is loaded
      if (!window.grecaptcha) {
        setSubmitStatus({ type: 'error', message: 'reCaptcha is not loaded. Please refresh the page and try again.' });
        setIsSubmitting(false);
        return;
      }

      // Wait for reCaptcha to be ready
      await new Promise((resolve) => {
        window.grecaptcha.ready(() => {
          resolve();
        });
      });

      // Get reCaptcha token
      const recaptchaToken = recaptchaWidgetId 
        ? window.grecaptcha.getResponse(recaptchaWidgetId)
        : window.grecaptcha.getResponse();
      
      console.log('reCaptcha token length:', recaptchaToken ? recaptchaToken.length : 0);
      console.log('reCaptcha token preview:', recaptchaToken ? recaptchaToken.substring(0, 50) + '...' : 'No token');
      console.log('Current domain:', window.location.hostname);
      console.log('Current protocol:', window.location.protocol);
      console.log('Using reCaptcha site key:', getRecaptchaSiteKey());
      
      if (!recaptchaToken) {
        setSubmitStatus({ type: 'error', message: 'Please complete the reCaptcha verification.' });
        setIsSubmitting(false);
        return;
      }

      // EmailJS configuration
      const serviceId = 'service_3mm691h';
      const templateId = 'template_jokidje';
      const publicKey = 'h48a_61N8xtG_gDQ_';
      
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_email: 'hello@customsync.app',
        'g-recaptcha-response': recaptchaToken
      };

      console.log('Sending data to EmailJS:', {
        service_id: serviceId,
        template_id: templateId,
        user_id: publicKey,
        template_params: {
          ...templateParams,
          'g-recaptcha-response': '[REDACTED - length: ' + recaptchaToken.length + ']'
        }
      });

      // Use direct API call (more reliable than SDK for reCaptcha)
      const data = {
        service_id: serviceId,
        template_id: templateId,
        user_id: publicKey,
        template_params: templateParams
      };

      console.log('Sending direct API request to EmailJS...');
      const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      console.log('Direct API response status:', response.status);
      const responseText = await response.text();
      console.log('Direct API response:', responseText);

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Message sent successfully! We\'ll get back to you soon.' });
        setFormData({ name: '', email: '', subject: '', message: '' });
        
        // Reset reCaptcha
        if (recaptchaWidgetId) {
          window.grecaptcha.reset(recaptchaWidgetId);
        } else {
          window.grecaptcha.reset();
        }
      } else {
        throw new Error(`EmailJS Error: ${response.status} - ${responseText}`);
      }
      
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title="Contact Us - CustomSync"
        description="Get in touch with the CustomSync team. We're here to help with your Amazon customization sync needs."
        keywords="contact CustomSync, support, help, Amazon customization, ShipStation, Veeqo"
        canonicalUrl="https://customsync.app/contact"
      />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Have questions about CustomSync? Need help with your integration? 
            We're here to help you streamline your Amazon customization workflow.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#3B79B3] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Email</h3>
                    <p className="text-gray-600">hello@customsync.app</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#3B79B3] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.759 8.071 16 9.007 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a3.998 3.998 0 01-.124-2.104l1.524-1.524a5.98 5.98 0 00.668 2.754l-1.524 1.525a3.997 3.997 0 01-.544-.651zM4.93 4.93a5.98 5.98 0 00-.668 2.754l1.524 1.525a3.997 3.997 0 01.124-2.104L4.93 4.93zm1.525 1.525a3.997 3.997 0 012.104-.124l1.524-1.524a5.98 5.98 0 00-2.754-.668L6.455 6.455zm6.09 6.09a3.997 3.997 0 01-2.104.124l-1.524 1.524a5.98 5.98 0 002.754.668l1.524-1.524z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Response Time</h3>
                    <p className="text-gray-600">We typically respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#3B79B3] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Address</h3>
                    <p className="text-gray-600">
                      1971 E Beltline Ave NE Ste 106 #1452<br />
                      Grand Rapids, MI 49525
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#3B79B3] rounded-lg p-6 text-white">
              <h3 className="text-lg font-semibold mb-3">Why Contact Us?</h3>
              <ul className="space-y-2 text-sm">
                <li>• Integration setup assistance</li>
                <li>• Custom configuration help</li>
                <li>• Technical support</li>
                <li>• Feature requests</li>
                <li>• Pricing inquiries</li>
              </ul>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-lg shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
            
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3B79B3] focus:border-transparent transition-colors"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3B79B3] focus:border-transparent transition-colors"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3B79B3] focus:border-transparent transition-colors"
                  placeholder="What's this about?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3B79B3] focus:border-transparent transition-colors resize-none"
                  placeholder="Tell us how we can help you..."
                />
              </div>

              {/* reCaptcha - Explicit rendering */}
              <div className="flex justify-center">
                <div id="recaptcha-container"></div>
              </div>

              {/* Submit Status */}
              {submitStatus && (
                <div className={`p-4 rounded-lg ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#3B79B3] hover:bg-[#2c5a8a] focus:ring-2 focus:ring-[#3B79B3] focus:ring-offset-2'
                }`}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;